import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { cx } from './classNames';
import { Portal } from './Portal';

/** Pendant zu `SfDialog` mit `DialogTemplates`/`FooterTemplate`. */
export interface DialogProps {
  open: boolean;
  onClose?: () => void;
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  /** z. B. `'450px'`, `'650px'`, `'435px'`. */
  width?: string;
  height?: string;
  /** Standard `true`. */
  isModal?: boolean;
  /** Standard `true`. */
  showCloseIcon?: boolean;
  /** Standard `true`. */
  closeOnEscape?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog(props: DialogProps) {
  const { onClose, open } = props;
  const isModal = props.isModal ?? true;
  const showCloseIcon = props.showCloseIcon ?? true;
  const closeOnEscape = props.closeOnEscape ?? true;
  const dialogRef = useRef<HTMLDivElement>(null);
  const headerId = useId();

  const focusable = useCallback(
    () => Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    [],
  );

  useEffect(() => {
    if (!open) return;
    const target = focusable()[0] ?? dialogRef.current;
    target?.focus();
  }, [focusable, open]);

  if (!open) return null;

  // Fokusfalle: Tab bleibt innerhalb des Dialogs.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && closeOnEscape) {
      event.stopPropagation();
      onClose?.();
      return;
    }
    if (event.key !== 'Tab') return;

    const nodes = focusable();
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (first === undefined || last === undefined) {
      event.preventDefault();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const hasHeader = props.header !== undefined || showCloseIcon;

  return (
    <Portal>
      {isModal ? <div className="e-dlg-overlay" /> : null}
      <div
        id={props.id}
        ref={dialogRef}
        role="dialog"
        aria-modal={isModal}
        aria-labelledby={props.header === undefined ? undefined : headerId}
        tabIndex={-1}
        title={props.title}
        className={cx('e-dialog', isModal && 'e-dlg-modal', props.className)}
        style={{
          width: props.width,
          height: props.height,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          ...props.style,
        }}
        onKeyDown={onKeyDown}
      >
        {hasHeader ? (
          <div className="e-dlg-header-content">
            <div className="e-dlg-header" id={headerId}>
              {props.header}
            </div>
            {showCloseIcon ? (
              <button
                type="button"
                className="e-btn e-flat e-dlg-closeicon-btn"
                aria-label="Schließen"
                onClick={onClose}
              >
                <span className="e-btn-icon e-icons e-icon-only e-icon-dlg-close fa-solid fa-xmark" />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="e-dlg-content">{props.children}</div>
        {props.footer === undefined ? null : <div className="e-footer-content">{props.footer}</div>}
      </div>
    </Portal>
  );
}

export default Dialog;
